import { useState } from 'react';
import { CircleDot, ExternalLink, RefreshCw, Search, ShieldAlert, Tag, Wrench } from 'lucide-react';
import { useFindings, useGithubIssues, useIssueFixRuns, dispatchIssueFix, type Finding, type RepoIssue, type IssueFixRun } from '../lib/data';
import { SkeletonTable } from './Skeleton';

const SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'];
const CATEGORIES = ['security', 'correctness', 'performance', 'test', 'style', 'docs', 'other'];
const STATUSES = ['open', 'resolved', 'wontfix'];

function severityTone(sev: string): { bg: string; fg: string } {
  switch (sev) {
    case 'critical':
    case 'high':
      return { bg: 'rgba(220,38,38,0.1)', fg: '#dc2626' };
    case 'medium':
      return { bg: 'rgba(217,119,6,0.12)', fg: '#b45309' };
    default:
      return { bg: 'var(--accent-bg)', fg: 'var(--accent)' };
  }
}

function Badge({ text, tone }: { text: string; tone: { bg: string; fg: string } }) {
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
      style={{ background: tone.bg, color: tone.fg }}
    >
      {text}
    </span>
  );
}

function selectCls(): string {
  return 'text-xs px-2 py-1.5 rounded-md border focus:outline-none';
}

export function Issues() {
  const [tab, setTab] = useState<'findings' | 'github' | 'fixes'>('findings');
  const [filters, setFilters] = useState<{ severity?: string; category?: string; status?: string }>({});

  return (
    <div className="space-y-5">
      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 rounded-lg w-fit" style={{ background: 'var(--ink-faint)' }}>
        {(['findings', 'github', 'fixes'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className="px-3 py-1.5 rounded-md text-xs font-semibold transition"
            style={{
              background: tab === t ? 'var(--bg)' : 'transparent',
              color: tab === t ? 'var(--ink)' : 'var(--ink-secondary)',
              boxShadow: tab === t ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
            }}
          >
            {t === 'findings' ? 'Review Findings' : t === 'github' ? 'GitHub Issues' : 'Fixes'}
          </button>
        ))}
      </div>

      {tab === 'findings' ? <FindingsTab filters={filters} setFilters={setFilters} /> : tab === 'github' ? <GithubTab /> : <FixesTab />}
    </div>
  );
}

function EmptyState({ icon: Icon, text, action }: { icon: typeof CircleDot; text: string; action?: React.ReactNode }) {
  return (
    <div className="p-10 text-center" style={{ color: 'var(--ink-secondary)' }}>
      <Icon className="w-6 h-6 mx-auto mb-2 opacity-60" />
      <div className="text-xs">{text}</div>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

function RetryButton({ onClick, loading }: { onClick: () => void; loading: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border disabled:opacity-50"
      style={{ borderColor: 'var(--border)', color: 'var(--ink)' }}
    >
      <RefreshCw className="w-3.5 h-3.5" /> Retry
    </button>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="overflow-hidden rounded-lg border" style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}>
      {children}
    </div>
  );
}

function FindingsTab({
  filters,
  setFilters,
}: {
  filters: { severity?: string; category?: string; status?: string };
  setFilters: (f: { severity?: string; category?: string; status?: string }) => void;
}) {
  const { findings, loading, error, retry } = useFindings(filters);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--ink-secondary)' }}>
          <Search className="w-3.5 h-3.5" />
          <span>Filter</span>
        </div>
        <select
          className={selectCls()}
          style={{ background: 'var(--bg)', borderColor: 'var(--border)', color: 'var(--ink)' }}
          value={filters.severity ?? ''}
          onChange={(e) => setFilters({ ...filters, severity: e.target.value || undefined })}
        >
          <option value="">All severities</option>
          {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          className={selectCls()}
          style={{ background: 'var(--bg)', borderColor: 'var(--border)', color: 'var(--ink)' }}
          value={filters.category ?? ''}
          onChange={(e) => setFilters({ ...filters, category: e.target.value || undefined })}
        >
          <option value="">All categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          className={selectCls()}
          style={{ background: 'var(--bg)', borderColor: 'var(--border)', color: 'var(--ink)' }}
          value={filters.status ?? ''}
          onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
        >
          <option value="">Any status</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <Card>
        {loading ? (
          <div style={{ padding: 12 }} aria-busy="true" aria-label="Loading findings">
            <SkeletonTable rows={6} cols={['22%', '46%', '12%', '10%', '10%']} />
          </div>
        ) : error ? (
          <EmptyState icon={ShieldAlert} text={error} action={<RetryButton onClick={retry} loading={loading} />} />
        ) : !findings || findings.length === 0 ? (
          <EmptyState icon={CircleDot} text="No findings yet. Run a review to populate this list." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider" style={{ color: 'var(--ink-secondary)' }}>
                  <th className="px-4 py-2.5 font-semibold">Source</th>
                  <th className="px-4 py-2.5 font-semibold">Finding</th>
                  <th className="px-4 py-2.5 font-semibold">Category</th>
                  <th className="px-4 py-2.5 font-semibold">Severity</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {findings.map((f) => <FindingRow key={f.id} f={f} />)}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function FindingRow({ f }: { f: Finding }) {
  const repoLabel = f.repoOwner && f.repoName ? `${f.repoOwner}/${f.repoName}#${f.prNumber ?? '?'}` : f.prId;
  const fileLabel = f.filepath ? `${f.filepath}${f.line ? `:${f.line}` : ''}` : '—';
  return (
    <tr className="border-t" style={{ borderColor: 'var(--border)' }}>
      <td className="px-4 py-2.5 align-top">
        <div className="font-mono text-xs" style={{ color: 'var(--ink)' }}>{repoLabel}</div>
        <div className="font-mono text-[10px]" style={{ color: 'var(--ink-secondary)' }}>{fileLabel}</div>
      </td>
      <td className="px-4 py-2.5 align-top max-w-md">
        <div className="text-xs" style={{ color: 'var(--ink)' }}>{f.message || <span style={{ color: 'var(--ink-secondary)' }}>—</span>}</div>
      </td>
      <td className="px-4 py-2.5 align-top">
        <Badge text={f.category} tone={{ bg: 'var(--ink-faint)', fg: 'var(--ink-secondary)' }} />
      </td>
      <td className="px-4 py-2.5 align-top">
        <Badge text={f.severity} tone={severityTone(f.severity)} />
      </td>
      <td className="px-4 py-2.5 align-top">
        <Badge
          text={f.status}
          tone={f.status === 'resolved' ? { bg: 'rgba(16,185,129,0.12)', fg: '#059669' } : { bg: 'var(--ink-faint)', fg: 'var(--ink-secondary)' }}
        />
      </td>
    </tr>
  );
}

function GithubTab() {
  const { issues, loading, error, retry } = useGithubIssues();
  const { retry: retryFixes } = useIssueFixRuns();
  const [dispatching, setDispatching] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const onFix = async (it: RepoIssue) => {
    const key = `${it.repo}#${it.number}`;
    setDispatching(key);
    setNotice(null);
    const [owner, repo] = it.repo.split('/');
    try {
      await dispatchIssueFix({ owner, repo, issueNumber: it.number, issueTitle: it.title });
      setNotice(`Fix task dispatched for ${key}. Track it in the Fixes tab.`);
      retryFixes();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : 'Failed to dispatch fix.');
    } finally {
      setDispatching(null);
    }
  };

  return (
    <div className="space-y-4">
      {notice && <div className="text-xs px-3 py-2 rounded-md" style={{ background: 'rgba(16,185,129,0.1)', color: '#059669' }}>{notice}</div>}
      <Card>
        {loading ? (
          <div style={{ padding: 12 }} aria-busy="true" aria-label="Loading GitHub issues">
            <SkeletonTable rows={6} cols={['20%', '52%', '16%', '12%']} />
          </div>
        ) : error ? (
          <EmptyState icon={ShieldAlert} text={error} action={<RetryButton onClick={retry} loading={loading} />} />
        ) : !issues || issues.length === 0 ? (
          <EmptyState icon={CircleDot} text="No open issues across installed repos (or the GitHub App isn't configured)." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider" style={{ color: 'var(--ink-secondary)' }}>
                  <th className="px-4 py-2.5 font-semibold">Repo</th>
                  <th className="px-4 py-2.5 font-semibold">Issue</th>
                  <th className="px-4 py-2.5 font-semibold">Labels</th>
                  <th className="px-4 py-2.5 font-semibold">Updated</th>
                  <th className="px-4 py-2.5 font-semibold">Fix</th>
                </tr>
              </thead>
              <tbody>
                {issues.map((it) => (
                  <RepoIssueRow key={`${it.repo}:${it.number}`} it={it}
                    onFix={onFix} dispatching={dispatching === `${it.repo}#${it.number}`} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function RepoIssueRow({ it, onFix, dispatching }: { it: RepoIssue; onFix: (it: RepoIssue) => void; dispatching: boolean }) {
  return (
    <tr className="border-t" style={{ borderColor: 'var(--border)' }}>
      <td className="px-4 py-2.5 align-top">
        <span className="font-mono text-xs" style={{ color: 'var(--ink-secondary)' }}>{it.repo}</span>
      </td>
      <td className="px-4 py-2.5 align-top max-w-md">
        <a href={it.htmlUrl} target="_blank" rel="noreferrer" className="group inline-flex items-start gap-1.5 min-w-0">
          <span className="font-mono text-xs shrink-0" style={{ color: 'var(--ink-secondary)' }}>#{it.number}</span>
          <span className="text-xs hover:underline truncate" style={{ color: 'var(--accent)' }}>{it.title}</span>
          <ExternalLink className="w-3 h-3 mt-0.5 shrink-0 opacity-0 group-hover:opacity-100" style={{ color: 'var(--ink-secondary)' }} />
        </a>
        {it.author && <div className="text-[10px] mt-0.5" style={{ color: 'var(--ink-secondary)' }}>@{it.author}</div>}
      </td>
      <td className="px-4 py-2.5 align-top">
        <div className="flex flex-wrap gap-1">
          {it.labels.slice(0, 3).map((l) => (
            <span key={l.name} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium" style={{ background: 'var(--ink-faint)', color: 'var(--ink-secondary)' }}>
              <Tag className="w-2.5 h-2.5" />
              {l.name}
            </span>
          ))}
        </div>
      </td>
      <td className="px-4 py-2.5 align-top">
        <span className="font-mono text-[11px]" style={{ color: 'var(--ink-secondary)' }}>
          {it.updatedAt ? new Date(it.updatedAt).toLocaleDateString() : '—'}
        </span>
      </td>
      <td className="px-4 py-2.5 align-top">
        <button
          onClick={() => onFix(it)}
          disabled={dispatching}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border disabled:opacity-50 transition"
          style={{ borderColor: 'var(--border)', color: 'var(--accent)', background: 'transparent' }}
        >
          <Wrench className="w-3 h-3" />
          {dispatching ? 'Dispatching…' : 'Fix'}
        </button>
      </td>
    </tr>
  );
}

function FixStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'completed':
      return <Badge text="PR open" tone={{ bg: 'rgba(16,185,129,0.12)', fg: '#059669' }} />;
    case 'failed':
    case 'timed_out':
      return <Badge text={status} tone={{ bg: 'rgba(220,38,38,0.1)', fg: '#dc2626' }} />;
    case 'running':
      return <Badge text="running" tone={{ bg: 'rgba(59,130,246,0.12)', fg: '#2563eb' }} />;
    default:
      return <Badge text={status} tone={{ bg: 'var(--ink-faint)', fg: 'var(--ink-secondary)' }} />;
  }
}

function FixesTab() {
  const { runs, loading, error, retry } = useIssueFixRuns();
  return (
    <Card>
      {loading ? (
        <div style={{ padding: 12 }} aria-busy="true" aria-label="Loading fix runs">
          <SkeletonTable rows={6} cols={['22%', '44%', '14%', '10%', '10%']} />
        </div>
      ) : error ? (
        <EmptyState icon={ShieldAlert} text={error} action={<RetryButton onClick={retry} loading={loading} />} />
      ) : !runs || runs.length === 0 ? (
        <EmptyState icon={CircleDot} text="No fix runs yet. Dispatch one from the GitHub Issues tab." />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider" style={{ color: 'var(--ink-secondary)' }}>
                <th className="px-4 py-2.5 font-semibold">Repo</th>
                <th className="px-4 py-2.5 font-semibold">Issue</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
                <th className="px-4 py-2.5 font-semibold">Branch</th>
                <th className="px-4 py-2.5 font-semibold">PR</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => <FixRunRow key={r.id} r={r} />)}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function FixRunRow({ r }: { r: IssueFixRun }) {
  return (
    <tr className="border-t" style={{ borderColor: 'var(--border)' }}>
      <td className="px-4 py-2.5 align-top">
        <span className="font-mono text-xs" style={{ color: 'var(--ink-secondary)' }}>{r.repoOwner}/{r.repoName}</span>
      </td>
      <td className="px-4 py-2.5 align-top max-w-md">
        <div className="font-mono text-xs" style={{ color: 'var(--ink)' }}>#{r.issueNumber}</div>
        <div className="text-[10px] mt-0.5 truncate" style={{ color: 'var(--ink-secondary)' }}>{r.issueTitle || '—'}</div>
        {r.error && <div className="text-[10px] mt-1" style={{ color: '#dc2626' }}>{r.error}</div>}
      </td>
      <td className="px-4 py-2.5 align-top">
        <FixStatusBadge status={r.status} />
      </td>
      <td className="px-4 py-2.5 align-top">
        <span className="font-mono text-[11px]" style={{ color: 'var(--ink-secondary)' }}>{r.branch ?? '—'}</span>
      </td>
      <td className="px-4 py-2.5 align-top">
        {r.prUrl ? (
          <a href={r.prUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs hover:underline" style={{ color: 'var(--accent)' }}>
            #{r.prNumber ?? ''} <ExternalLink className="w-3 h-3" />
          </a>
        ) : <span style={{ color: 'var(--ink-secondary)' }}>—</span>}
      </td>
    </tr>
  );
}
